import { z } from "zod";
import { EDocumentKey, EDocumentList } from "./EDocumentKey";
import { allKeysOptionalI9DocumentZodSchema } from "./zod-schemas";

export type TI9DocumentData = z.infer<
  typeof allKeysOptionalI9DocumentZodSchema
>;

export enum EI9DocumentPhotoSide {
  FRONT = "FRONT",
  BACK = "BACK",
  OTHER = "OTHER",
}

export const I9SupplementBDocumentPrefix = "supplement_b_";

export interface II9DocumentPhotos {
  doc_url?: string;
  doc_base64?: string;
  doc_blob?: Buffer;
  description?: string;
  side?: EI9DocumentPhotoSide;
  file_suffix?: number; // number appended to EDocumentKey to get URL
}

export const VeriffDecisionStatus = {
  APPROVED: "approved",
  DECLINED: "declined",
  RESUBMISSION_REQUESTED: "resubmission_requested",
  REVIEW: "review",
  EXPIRED: "expired",
  ABANDONED: "abandoned",
} as const;
export type VeriffDecisionStatus =
  (typeof VeriffDecisionStatus)[keyof typeof VeriffDecisionStatus];

export const PersonaDecisionStatus = {
  PASSED: "passed",
  FAILED: "failed",
  CANCELED: "canceled",
} as const;
export type PersonaDecisionStatus =
  (typeof PersonaDecisionStatus)[keyof typeof PersonaDecisionStatus];

export const veriffIDVDecisionResponseSchema = z.object({
  veriffID: z.string(),
  workerID: z.string(),
  companyID: z.string(),
  status: z.nativeEnum(VeriffDecisionStatus),
  reason: z.string().optional(),
  reasonCode: z.number().optional(),
  riskLabels: z.array(z.string()).optional(),
  riskScore: z.number().optional(),
  document: z
    .object({
      number: z.string(),
      type: z.string(),
      country: z.string(),
      validFrom: z.string(),
      validUntil: z.string(),
      state: z.string(),
    })
    .optional(),
});

export type VeriffIDVDecisionResponse = z.infer<
  typeof veriffIDVDecisionResponseSchema
>;

export const personaIDVDecisionResponseSchema = z.object({
  persona_inquiry_id: z.string(),
  persona_verification_id: z.string(),
  document_key: z.nativeEnum(EDocumentKey).optional(),
  workerID: z.string(),
  companyID: z.string(),
  status: z.nativeEnum(PersonaDecisionStatus),
  checks: z
    .array(
      z.object({
        name: z.string(),
        reasons: z.array(z.string()),
      })
    )
    .optional(),
});

export type PersonaIDVDecisionResponse = z.infer<
  typeof personaIDVDecisionResponseSchema
>;

export interface II9Document {
  document_key: EDocumentKey;
  document_list: EDocumentList;
  document_data?: TI9DocumentData; // Optional when the worker has not yet provided the document details yet
  document_photos?: II9DocumentPhotos[]; // Optional when the worker has not yet provided the document photos yet
  veriff_id?: string; // Required only when the worker goes through Veriff IDV
  persona_inquiry_id?: string; // Required only when the worker goes through Persona
}

export interface II9DocumentTypeFormDataProps {
  document_type_a_code?: EDocumentKey;
  document_type_b_code?: EDocumentKey;
  document_type_c_code?: EDocumentKey;
}
